import * as Yup from "yup";

import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";

import CustomAlert from "../../../components/CustomAlert";
import CustomDialog from "../../../components/CustomDialog";
import { Autocomplete as FormikAutocomplete } from "formik-material-ui-lab";
import { KeyboardDatePicker as FormikKeyboardDatePicker } from "formik-material-ui-pickers";
import { TextField as FormikTextField } from "formik-material-ui";
import dayjs from "dayjs";
import { fetchLeaveReasons } from "../../../../services/leaveReasonsService";
import { fetchPortNames } from "../../../../services/portNamesServices";
import { fetchSailorSpecialization } from "../../../../services/sailorSpecializationService";
import { updateShipRecordDetailsService } from "../../../../services/shipRecordDetailsService";
import useLocale from "../../../../hooks/useLocale";
import GovGrModal from "../../../../components/GovGrModal";
import GovGrTextField from "../../../../components/GovGrTextField";
import GovGrSelectBox from "../../../../components/GovGrSelectBox";
import GovGrDate from "../../../../components/GovGrDate";
import GovGrButton from "../../../../components/GovGrButton";

function UpdateDialog({ open, close, refetch, selectedItem, crewlist }) {
  const { t } = useLocale();
  const [error, setError] = useState("");

  const sailorFormRequireAllValidation = Yup.object().shape({
    start_dt: Yup.date().nullable().required(t("Field is required")),
    leave_dt: Yup.date().nullable().required(t("Field is required")),
    specialty: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
    leave_reason: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
    leave_place: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
  });

  const sailorCrewRequiredOneValidation = Yup.object().shape({
    start_dt: Yup.date().required(t("Field is required")).nullable(),
    specialty: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
    leave_dt: Yup.date().nullable(),
    leave_place: Yup.object().nullable(),
    leave_reason: Yup.object().nullable(),
  });

  const [currentValidation, setCurrentValidation] = useState(
    sailorCrewRequiredOneValidation
  );

  const changeValidator = (propertyName, value, setFieldValue) => {
    setFieldValue(propertyName, value);

    if (value) {
      setCurrentValidation(sailorFormRequireAllValidation);
    } else {
      setCurrentValidation(sailorCrewRequiredOneValidation);
    }
  };

  // Fetch Leave Reasons
  const { data: leaveReasons } = useQuery("leaveReasons", fetchLeaveReasons);

  // Fetch Ports Names
  const { data: portNames } = useQuery("portNames", fetchPortNames);

  // Fetch Sailor Specializations
  const { data: sailorSpecialization } = useQuery(
    "sailorSpecialization",
    fetchSailorSpecialization
  );

  // Mutation for updating data
  const { mutate } = useMutation(updateShipRecordDetailsService, {
    onSuccess: (data) => {
      refetch();
      close();
    },
    onError: (error) => {
      switch (error) {
        default: {
          setError(t("A network error occured. Please try again later"));
        }
      }
    },
  });

  // Initial values
  const initialValues = {
    id: selectedItem.r_id,
    sailor_name: selectedItem.s_first_name + " " + selectedItem.s_last_name,
    ameth: selectedItem.s_ameth,
    specialty: selectedItem.specialty,
    ship_record_hd_id: parseInt(crewlist),
    start_place: selectedItem.start_place,
    sailor_id: selectedItem.s_id,
    start_dt: new Date(selectedItem.r_start_dt),
    leave_reason: selectedItem.leave_reason, //leave reason
    leave_place: selectedItem.leave_place, //port
    leave_dt: new Date(selectedItem.r_leave_dt),
  };

  const handleUpdateItem = (values) => {
    let createData;
    const {
      leave_dt,
      start_dt,
      leave_reason,
      leave_place,
      start_place,
      sailor_name,
      ameth,
      specialty,
      ...rest
    } = values;

    if (leave_dt) {
      createData = {
        ...rest,
        start_dt: dayjs(start_dt).format("YYYY-MM-DD"),
        start_place_id: start_place.id,
        leave_place_id: leave_place.id,
        leave_reason_id: leave_reason.id,
        leave_dt: dayjs(leave_dt).format("YYYY-MM-DD"),
      };
    } else {
      createData = {
        ...rest,
        start_dt: dayjs(start_dt).format("YYYY-MM-DD"),
        start_place_id: start_place.id,
        leave_place_id: null,
        leave_reason_id: null,
        leave_dt: null,
      };
    }

    mutate({ id: selectedItem.id, ...createData });
  };

  return (
    <>
      <GovGrModal customContent="true" open={open} onClose={close}>
        <div className="govgr-heading-l m-0">{t("Edit Crew Member")}</div>
        <CustomAlert
          open={!!error}
          close={() => {
            setError("");
          }}
          message={t("A network error occured, please try again later")}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={currentValidation}
          enableReinitialize={true}
          onSubmit={(values, { setSubmitting }) => {
            handleUpdateItem(values);
            setSubmitting(false);
          }}
        >
          {({ setFieldValue, errors, touched, submitForm }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <GovGrTextField
                      name="sailor_name"
                      disabled
                      label={t("Full Sailor Name")}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <GovGrTextField name="ameth" disabled label={t("Ameth")} />
                  </Grid>
                </Grid>

                <GovGrSelectBox
                  name="specialty"
                  options={sailorSpecialization || []}
                  optionlabel="name"
                  label={t("Sailor Specializations")}
                />

                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <GovGrDate
                      name="start_dt"
                      label={t("Nautical Recruitment Date")}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <GovGrSelectBox
                      name="start_place"
                      options={portNames || []}
                      optionlabel="name"
                      label={t("Start Place")}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <GovGrDate name="leave_dt" label={t("Leave Date")} />
                  </Grid>
                  <Grid item xs={6}>
                    <GovGrSelectBox
                      name="leave_reason"
                      options={leaveReasons || []}
                      optionlabel="name"
                      label={t("Leave Reason")}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <GovGrSelectBox
                      name="leave_place"
                      options={portNames || []}
                      optionlabel="name"
                      label={t("Leave Place")}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <GovGrButton
                  onClick={submitForm}
                  variant="primary"
                  buttonText={t("Update")}
                />
                <GovGrButton
                  onClick={close}
                  variant="secondary"
                  buttonText={t("Cancel")}
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </GovGrModal>
    </>
  );
}

export default UpdateDialog;
